/**
 * Embedded Signup da Meta (WhatsApp Cloud API) — v5, modo coexistência.
 *
 * Coexistência = o número continua funcionando no app WhatsApp Business do
 * celular da loja E na Cloud API ao mesmo tempo. Por isso o fluxo usa
 * `featureType: whatsapp_business_app_onboarding` e o registro do número é
 * pulado no backend (ele já está registrado).
 *
 * Requisitos da Meta:
 *  - session logging obrigatório nesse modo (é o listener de `message` abaixo)
 *  - app WhatsApp Business do franqueado na versão 2.24.17 ou superior
 *
 * Variáveis de ambiente:
 *  REACT_APP_META_APP_ID
 *  REACT_APP_META_CONFIG_ID
 *  REACT_APP_META_GRAPH_VERSION (opcional, default v23.0)
 */

const APP_ID = process.env.REACT_APP_META_APP_ID
const CONFIG_ID = process.env.REACT_APP_META_CONFIG_ID
const GRAPH_VERSION = process.env.REACT_APP_META_GRAPH_VERSION || 'v23.0'

let sdkPromise = null

export function isEmbeddedSignupConfigured() {
  return Boolean(APP_ID && CONFIG_ID)
}

function loadFacebookSdk() {
  if (sdkPromise) return sdkPromise

  sdkPromise = new Promise((resolve, reject) => {
    if (window.FB) return resolve(window.FB)

    window.fbAsyncInit = function () {
      window.FB.init({
        appId: APP_ID,
        cookie: true,
        xfbml: false,
        version: GRAPH_VERSION,
      })
      resolve(window.FB)
    }

    const existing = document.getElementById('facebook-jssdk')
    if (existing) return

    const script = document.createElement('script')
    script.id = 'facebook-jssdk'
    script.src = 'https://connect.facebook.net/en_US/sdk.js'
    script.async = true
    script.defer = true
    script.crossOrigin = 'anonymous'
    script.onerror = () => {
      sdkPromise = null
      reject(new Error('Não foi possível carregar o SDK da Meta'))
    }
    document.body.appendChild(script)
  })

  return sdkPromise
}

/**
 * Abre o popup do Embedded Signup.
 *
 * Resolve com { code, wabaId, phoneNumberId }.
 * O `code` vem do callback do FB.login; o `wabaId` e o `phoneNumberId` vêm do
 * session logging (evento `message` com type WA_EMBEDDED_SIGNUP).
 */
export async function launchEmbeddedSignup() {
  if (!isEmbeddedSignupConfigured()) {
    throw new Error('Embedded Signup não configurado (APP_ID / CONFIG_ID ausentes)')
  }

  const FB = await loadFacebookSdk()

  return new Promise((resolve, reject) => {
    let sessionInfo = {}

    const onMessage = (event) => {
      if (!String(event.origin).endsWith('facebook.com')) return
      try {
        const payload = JSON.parse(event.data)
        if (payload.type !== 'WA_EMBEDDED_SIGNUP') return

        if (payload.event === 'FINISH' || payload.event === 'FINISH_ONLY_WABA') {
          sessionInfo = payload.data || {}
        }

        // Coexistência: a Meta devolve este evento quando converte a conta
        // existente do app WhatsApp Business. O waba_id é o mesmo de antes.
        if (payload.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
          sessionInfo = { ...(payload.data || {}), coexistence: true }
        }

        if (payload.event === 'CANCEL') {
          cleanup()
          reject(new Error(payload.data?.error_message || 'Cadastro cancelado'))
        }
      } catch (_) {
        // mensagens que não são JSON são de outros fluxos da Meta — ignora
      }
    }

    const cleanup = () => window.removeEventListener('message', onMessage)

    window.addEventListener('message', onMessage)

    FB.login(
      (response) => {
        cleanup()
        const code = response?.authResponse?.code
        if (!code) {
          return reject(new Error('Cadastro não concluído'))
        }
        resolve({
          code,
          wabaId: sessionInfo.waba_id || null,
          phoneNumberId: sessionInfo.phone_number_id || null,
          coexistence: Boolean(sessionInfo.coexistence),
        })
      },
      {
        config_id: CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: 'whatsapp_business_app_onboarding',
          sessionInfoVersion: '3',
        },
      },
    )
  })
}
